import type { ColumnDef } from '@tanstack/react-table'
import classNames from 'classnames'
import { useState } from 'react'

import AtomNotice from '@/components/Atoms/AtomNotice/AtomNotice'
import AtomTable from '@/components/Atoms/AtomTable/AtomTable'
import LabelHint from '@/components/Atoms/LabelHint/LabelHint'
import { trimTrailingZeros } from '@/utils/format/trimTrailingZeros'

import {
  formatPriceRatio,
  PRICE_CANCEL_REASON_LABELS,
} from '../../../lib/stockImportsPriceLabels'
import type {
  CalibrationExampleDto,
  PriceSummaryDto,
  SegmentCalibrationDto,
} from '../../../types/stockImportsPrices.types'
import cn from './PricePreviewSummary.module.scss'

const CALIBRATION_HINT =
  '1,00 — цена из файла совпадает с текущей ценой УТ. Подберите скидку к закупке так, чтобы отношение к «Базовой» было около 1,00, затем наценки сегментов'

const CALIBRATION_WIDTHS: Partial<Record<string, string>> = {
  segment: 'minmax(160px, 1fr)',
  comparedProducts: '110px',
  medianRatio: '110px',
  deviationPercent: '120px',
}

const EXAMPLE_WIDTHS: Partial<Record<string, string>> = {
  article: 'minmax(110px, 0.8fr)',
  description: 'minmax(200px, 2fr)',
  filePrice: '110px',
  erpPrice: '110px',
  ratio: '100px',
}

const EXAMPLE_COLUMNS: ColumnDef<CalibrationExampleDto, unknown>[] = [
  { id: 'article', header: 'Артикул', cell: ({ row }) => row.original.article },
  {
    id: 'description',
    header: 'Наименование',
    cell: ({ row }) => row.original.description,
  },
  {
    id: 'filePrice',
    header: 'Из файла',
    cell: ({ row }) => trimTrailingZeros(row.original.filePrice),
  },
  {
    id: 'erpPrice',
    header: 'УТ',
    cell: ({ row }) => trimTrailingZeros(row.original.erpPrice),
  },
  {
    id: 'ratio',
    header: 'Отношение',
    cell: ({ row }) => formatPriceRatio(row.original.ratio),
  },
]

const buildCalibrationColumns = (
  onSelect: (segmentCode: number) => void
): ColumnDef<SegmentCalibrationDto, unknown>[] => [
  {
    id: 'segment',
    header: 'Сегмент',
    cell: ({ row }) => (
      <button
        type="button"
        className={cn.segmentButton}
        onClick={() => onSelect(row.original.segmentCode)}
      >
        {row.original.segmentDescription}
      </button>
    ),
  },
  {
    id: 'comparedProducts',
    header: 'Товаров',
    cell: ({ row }) => row.original.comparedProducts,
  },
  {
    id: 'medianRatio',
    header: 'Отношение',
    cell: ({ row }) => formatPriceRatio(row.original.medianRatio),
  },
  {
    id: 'deviationPercent',
    header: 'Отклонение, %',
    cell: ({ row }) => (
      <span
        className={classNames(row.original.isDeviationHigh && cn.deviationHigh)}
      >
        {row.original.deviationPercent === undefined
          ? '—'
          : trimTrailingZeros(row.original.deviationPercent)}
      </span>
    ),
  },
]

interface PricePreviewSummaryProps {
  summary: PriceSummaryDto
  calibration: SegmentCalibrationDto[]
  isOrganisationSelected: boolean
}

function PricePreviewSummary({
  summary,
  calibration,
  isOrganisationSelected,
}: PricePreviewSummaryProps) {
  const [selectedSegmentCode, setSelectedSegmentCode] = useState<number>()
  const selectedSegment = calibration.find(
    (item) => item.segmentCode === selectedSegmentCode
  )

  return (
    <section className={cn.summary} aria-label="Сверка с УТ">
      <h4 className={cn.title}>
        Сверка с УТ <LabelHint text={CALIBRATION_HINT} />
      </h4>
      <p className={cn.line}>
        Закупка / «Базовая» УТ: {formatPriceRatio(summary.basePriceMedianRatio)}{' '}
        ({summary.basePriceComparedProducts} товаров)
      </p>
      <p className={cn.line}>
        С ценой: {summary.pricedProducts} из {summary.sampleRows} строк · без
        закупки: {summary.priceInvalidRows}
      </p>
      {summary.cancelReason && (
        <AtomNotice
          type="error"
          description={PRICE_CANCEL_REASON_LABELS[summary.cancelReason]}
        >
          Цены при загрузке не применятся
        </AtomNotice>
      )}
      {summary.missingRateCurrencies.length > 0 && (
        <AtomNotice type="warning">
          Нет курса НБРБ на сегодня для валют:{' '}
          {summary.missingRateCurrencies.join(', ')}
        </AtomNotice>
      )}
      <AtomTable
        data={calibration}
        columns={buildCalibrationColumns(setSelectedSegmentCode)}
        getRowId={(item) => String(item.segmentCode)}
        columnWidths={CALIBRATION_WIDTHS}
        isOrganisationSelected={isOrganisationSelected}
        getRowClassName={(item) =>
          item.isDeviationHigh ? cn.rowDeviationHigh : undefined
        }
        emptyContent="Нет сегментов для сверки"
        ariaLabel="Сверка цен по сегментам"
      />
      {selectedSegment && (
        <AtomTable
          data={selectedSegment.examples}
          columns={EXAMPLE_COLUMNS}
          getRowId={(example) => String(example.productCode)}
          columnWidths={EXAMPLE_WIDTHS}
          isOrganisationSelected={isOrganisationSelected}
          emptyContent="Нет примеров"
          ariaLabel={`Примеры: ${selectedSegment.segmentDescription}`}
        />
      )}
    </section>
  )
}

export default PricePreviewSummary
